import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Music,
  X,
  ChevronDown,
  AlertTriangle,
  AlertCircle,
  Play,
  Pause,
  Headphones,
  Mic,
  StopCircle,
  Upload,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  clientSubmitOrderDetails,
  getClientOrderById,
} from "../../redux/reduxSlices/ordersSlice";
import { toneOptions } from "../../utils/util";

const ClientJingleDetailsForm = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isToneDropdownOpen, setIsToneDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toneDropdownRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRecordingPopup, setShowRecordingPopup] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState(null);
  const audioRef = useRef(null);
  const [playingTone, setPlayingTone] = useState(null);

  const { clientOrderByIdData, loading } = useSelector((state) => state.orders);

  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [orderBasics, setOrderBasics] = useState(null);
  const [orderNotFound, setOrderNotFound] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Audio player states for existing audio
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const existingAudioRef = useRef(null);
  const [isUploadedAudioPlaying, setIsUploadedAudioPlaying] = useState(false);
  const uploadedAudioRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setRecordingTime(0);

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `${id}_brand_name_audio.webm`, {
          type: "audio/webm",
        });

        setAudioFile(file);
        setShowRecordingPopup(false);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());

        // Clear timer
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
      };

      mediaRecorder.start();
      setRecording(true);
      setShowRecordingPopup(true);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const closeRecordingPopup = () => {
    if (recording) {
      stopRecording();
    } else {
      setShowRecordingPopup(false);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlay = (tone) => {
    if (!tone.url) return;

    if (playingTone === tone.name) {
      audioRef.current.pause();
      setPlayingTone(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(tone.url);
      audioRef.current = audio;

      audio.play();
      setPlayingTone(tone.name);

      audio.onended = () => {
        setPlayingTone(null);
      };
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingTone(null);
    }
  };

  // Audio player functions for existing audio
  const togglePlayPause = () => {
    if (!clientOrderByIdData?.order?.brand_name_audio?.url) return;

    if (isPlaying) {
      existingAudioRef.current.pause();
      setIsPlaying(false);
    } else {
      existingAudioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error("Playback failed:", err);
          toast.error("Failed to play audio");
        });
    }
  };

  const handleTimeUpdate = () => {
    if (existingAudioRef.current) {
      setCurrentTime(existingAudioRef.current.currentTime);
      setAudioProgress(
        (existingAudioRef.current.currentTime /
          existingAudioRef.current.duration) *
          100,
      );
    }
  };

  const handleLoadedMetadata = () => {
    if (existingAudioRef.current) {
      setAudioDuration(existingAudioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (existingAudioRef.current) {
      const seekTime =
        (parseFloat(e.target.value) / 100) * existingAudioRef.current.duration;
      existingAudioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
      setAudioProgress(parseFloat(e.target.value));
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioProgress(0);
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === null || time === undefined) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!isToneDropdownOpen) {
      stopAudio();
    }
  }, [isToneDropdownOpen]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (existingAudioRef.current) {
        existingAudioRef.current.pause();
      }
      if (uploadedAudioRef.current) {
        uploadedAudioRef.current.pause();
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }

      if (
        toneDropdownRef.current &&
        !toneDropdownRef.current.contains(event.target)
      ) {
        setIsToneDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const industryCategories = useMemo(
    () => [
      "Food & Beverage",
      "Technology",
      "Fashion",
      "Healthcare",
      "Education",
      "Real Estate",
      "Automotive",
      "Entertainment",
      "Retail",
      "Finance",
      "Travel & Tourism",
      "Others",
    ],
    [],
  );

  const voiceOptions = useMemo(
    () => ["Male Voice", "Female Voice", "Male & Female"],
    [],
  );

  const targetAudienceOptions = ["Children", "Teenagers", "Adults", "Seniors"];

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      companyName: "",
      gstNumber: "",
      companyAddress: "",
      companyCity: "",
      brandName: "",
      website: "",
      instagramLink: "",
      companyDetails: "",
      preferredLanguage: "",
      industryCategory: "",
      targetAudience: [],
      tone: "",
      voicePreference: "",
      tagline: "",
      keywords: "",
      restrictions: "",
      jingleRequirements: "",
    },
  });

  // Fetch order
  useEffect(() => {
    if (id) {
      dispatch(getClientOrderById(id));
    }
  }, [id, dispatch]);

  // Handle API response and prefill form
  useEffect(() => {
    if (!clientOrderByIdData) return;

    // Check if order was not found
    if (clientOrderByIdData.success === false) {
      setOrderNotFound(true);
      return;
    }

    // Reset not found state if order exists
    setOrderNotFound(false);

    const order = clientOrderByIdData.order;
    if (!order || !id) return;

    // lock condition (server should enforce also)
    const alreadySubmitted = !!order?.client_form_submitted;
    setLocked(alreadySubmitted);

    const targetAudienceArr = order.target_audience || [];

    // Basics
    setOrderBasics({
      name: order?.name || "",
      email: order?.email || "",
      mobile: order?.mobile_number || "",
      order_id: order?.order_id || id,
      song_duration: order?.song_duration || "",
    });

    // keywords can be array or string; normalize to string for textarea
    const keywordsText = Array.isArray(order?.keywords_to_highlight)
      ? order.keywords_to_highlight.join(", ")
      : typeof order?.keywords_to_highlight === "string"
        ? order.keywords_to_highlight
        : "";

    reset({
      companyName: order?.company_name || "",
      gstNumber: order?.gst_number || "",
      companyAddress: order?.company_address || "",
      companyCity: order?.company_city || "",
      brandName: order?.brand_name_in_jingle || "",
      website: order?.website || "",
      instagramLink: order?.instagram_link || "",
      companyDetails: order?.company_details || "",
      preferredLanguage: order?.preferred_language || "",
      industryCategory: order?.brand_category || "",
      targetAudience: targetAudienceArr,
      tone: order?.tone || "",
      voicePreference: order?.voice_preference || "",
      tagline: order?.tagline_or_slogan || "",
      keywords: keywordsText,
      restrictions: order?.restrictions || "",
      jingleRequirements: order?.jingle_requirements || "",
    });
  }, [clientOrderByIdData, id, reset]);

  const preparePayload = (data) => {
    return {
      // optional
      company_name: data.companyName?.trim() || "",
      gst_number: data.gstNumber?.trim() || "",
      company_address: data.companyAddress?.trim() || "",
      website: data.website?.trim() || "",
      instagram_link: data.instagramLink?.trim() || "",
      timeline: data.timeline?.trim() || "",
      jingle_requirements: data.jingleRequirements?.trim() || "",

      // ✅ required (match backend keys exactly)
      company_city: data.companyCity?.trim(),
      brand_name_in_jingle: data.brandName?.trim(),
      company_details: data.companyDetails?.trim(),
      preferred_language: data.preferredLanguage?.trim(),
      brand_category: data.industryCategory?.trim(),

      // arrays
      target_audience: Array.isArray(data.targetAudience)
        ? data.targetAudience
        : (data.targetAudience || "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),

      tone: data.tone?.trim(),
      voice_preference: data.voicePreference?.trim(),
      tagline_or_slogan: data.tagline?.trim(),

      keywords_to_highlight: (data.keywords || "")
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean),

      restrictions: data.restrictions?.trim(),
    };
  };

  const handleFormSubmit = async (data) => {
    if (locked) return;

    const payload = preparePayload(data);
    setFormDataToSubmit({ id, payload, audioFile });
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    if (!formDataToSubmit) return;

    try {
      setSubmitting(true);
      const res = await dispatch(
        clientSubmitOrderDetails({
          id: formDataToSubmit.id,
          payload: formDataToSubmit.payload,
          audioFile: formDataToSubmit.audioFile,
        }),
      ).unwrap();

      if (res?.success) {
        toast.success("Submitted successfully. This form is now locked.");
        setLocked(true);
        setShowConfirmModal(false);
      } else {
        toast.error(res?.detail || "Failed to submit");
      }
    } catch (e) {
      console.log("error", e);
      toast.error(e?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
      setFormDataToSubmit(null);
    }
  };

  const cancelSubmit = () => {
    setShowConfirmModal(false);
    setFormDataToSubmit(null);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  // Show not found state
  if (orderNotFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              Order Not Found
            </h2>
            <p className="text-gray-600 text-center mb-6">
              {clientOrderByIdData?.message ||
                "The order you are looking for does not exist or you don't have permission to view it."}
            </p>
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
              <p className="text-sm text-red-800 text-center">
                Order ID: <span className="font-mono font-medium">{id}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const existingAudio = clientOrderByIdData?.order?.brand_name_audio;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Hidden audio element for existing audio */}
      {existingAudio?.url && !locked && (
        <audio
          ref={existingAudioRef}
          src={existingAudio.url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleAudioEnded}
        />
      )}

      {/* Recording Popup Modal */}
      {showRecordingPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
                  <Mic className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {recording ? "Recording in progress..." : "Ready to record"}
                </h3>
              </div>
              <button
                onClick={closeRecordingPopup}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="text-center py-6">
              {recording ? (
                <>
                  <div className="mb-4">
                    <div className="w-24 h-24 mx-auto rounded-full bg-red-50 flex items-center justify-center border-4 border-red-200 animate-pulse">
                      <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                        <Mic className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-3xl font-mono font-bold text-red-600">
                      {formatRecordingTime(recordingTime)}
                    </div>
                    <p className="text-sm text-gray-600">
                      Speak clearly into your microphone
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="w-20 h-20 mx-auto rounded-full bg-orange-100 flex items-center justify-center">
                    <Mic className="w-10 h-10 text-orange-600" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Click the button below when you're ready to start recording
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-center gap-3">
              {recording ? (
                <button
                  onClick={stopRecording}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                >
                  <StopCircle className="w-5 h-5" />
                  Stop Recording
                </button>
              ) : (
                <button
                  onClick={startRecording}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                >
                  <Mic className="w-5 h-5" />
                  Start Recording
                </button>
              )}
            </div>

            {!recording && (
              <p className="text-center text-xs text-gray-500 mt-4">
                Make sure your microphone is connected and permissions are
                granted
              </p>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirm Submission
                </h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to submit the form? Once submitted, you
                  cannot make any changes. Please review all information
                  carefully.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-xs text-yellow-800 font-medium">
                ⚠️ This action is irreversible. The form will be locked for
                editing.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelSubmit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSubmit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Submitting...
                  </>
                ) : (
                  "Yes, Submit Form"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between ">
          <h1 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Music className="w-4 h-4 text-orange-600 max-md:hidden" />
            Jingle Details (Order {orderBasics?.order_id || id})
          </h1>

          {locked ? (
            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 border border-green-200">
              Submitted
            </span>
          ) : (
            <span className="text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200">
              Please fill and submit once
            </span>
          )}
        </div>
        <div className="p-2 border-b border-gray-200 flex flex-col items-start justify-between">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            Instructions
          </h3>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>
              Once you fill out the form, we will share the lyrics for your
              approval.
            </li>
            <li>
              After the lyrics are approved, we will create and share three
              versions of the jingle based on the same lyrics.
            </li>
          </ul>
        </div>

        {/* Basic Info */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 max-md:gap-1.5 text-sm">
            <div>
              <div className="text-xs text-gray-500">Name</div>
              <div className="font-medium text-gray-900">
                {orderBasics?.name || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Email</div>
              <div className="font-medium text-gray-900">
                {orderBasics?.email || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Mobile</div>
              <div className="font-medium text-gray-900">
                {orderBasics?.mobile || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Song Duration</div>
              <div className="font-medium text-gray-900">
                {orderBasics?.song_duration || "-"}
              </div>
            </div>
          </div>
        </div>

        {locked && (
          <div className="p-4">
            <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              You've successfully submitted this form. If you have any changes
              please contact sales co-ordinator.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4">
          <div className="space-y-6">
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                Company & Brand
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Company Name{" "}
                    <span className="text-sm font-normal text-gray-500">
                      (ex: Digikore/ Starbucks)
                    </span>
                  </label>
                  <input
                    type="text"
                    {...register("companyName")}
                    disabled={locked || loading}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your GST Number{" "}
                    <span className="text-sm font-normal text-gray-500">
                      (ex: 27ABCDE1234F2Z5)
                    </span>
                  </label>
                  <input
                    type="text"
                    {...register("gstNumber")}
                    disabled={locked || loading}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Company City{" "}
                    <span className="text-red-600">*</span>{" "}
                    <span className="text-sm font-normal text-gray-500">
                      (ex: Pune)
                    </span>
                  </label>
                  <input
                    type="text"
                    {...register("companyCity", {
                      required: "Company city is required",
                    })}
                    disabled={locked || loading}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                      errors.companyCity
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.companyCity && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.companyCity.message}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your detailed Company Address
                  </label>
                  <textarea
                    {...register("companyAddress")}
                    rows={2}
                    disabled={locked || loading}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Brand Name for Jingle
                    <span className="text-red-600"> *</span>
                    <span className="text-sm font-normal text-gray-500">
                      (ex: Amul)
                    </span>
                  </label>

                  <input
                    type="text"
                    {...register("brandName", {
                      required: "Brand name is required",
                    })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                    placeholder="Brand name for jingle"
                    disabled={isLoading || locked}
                  />
                  <span className="text-sm font-normal text-gray-500">
                    Record your Brand Name or upload a Brand Name audio file for
                    exact pronunciation in the jingle. You can also press the
                    Record button to record it directly.
                  </span>

                  {/* Show existing audio player if available */}
                  {existingAudio?.url && !locked && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Headphones className="w-4 h-4 text-orange-600" />
                        <span className="text-xs font-semibold text-orange-700">
                          Existing Pronunciation
                        </span>
                        <span className="text-xs text-gray-500">
                          ({existingAudio.format})
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={togglePlayPause}
                          className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-center hover:shadow-md transition-shadow"
                        >
                          {isPlaying ? (
                            <Pause className="w-5 h-5" />
                          ) : (
                            <Play className="w-5 h-5 ml-0.5" />
                          )}
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(audioDuration)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={audioProgress}
                            onChange={handleSeek}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #f97316 0%, #f97316 ${audioProgress}%, #e5e7eb ${audioProgress}%, #e5e7eb 100%)`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        File: {existingAudio.filename}
                        {existingAudio.uploaded_at && (
                          <span className="ml-2">
                            • Uploaded:{" "}
                            {new Date(
                              existingAudio.uploaded_at,
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recording/Upload buttons - only show if form is not locked */}
                  {!locked && (
                    <>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {!recording ? (
                          <button
                            type="button"
                            onClick={startRecording}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                          >
                            <Mic className="w-3.5 h-3.5" />
                            Record
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                          >
                            <StopCircle className="w-3.5 h-3.5" />
                            Stop Recording
                          </button>
                        )}

                        <span className="text-xs text-gray-400">or</span>

                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors cursor-pointer">
                          <Upload className="w-3.5 h-3.5" />
                          <span>Upload Audio</span>
                          <input
                            type="file"
                            accept="audio/*"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Clear any previous audio
                                if (uploadedAudioRef.current) {
                                  uploadedAudioRef.current.pause();
                                  uploadedAudioRef.current.removeAttribute(
                                    "src",
                                  );
                                  uploadedAudioRef.current.load();
                                  setIsUploadedAudioPlaying(false);
                                }
                                setAudioFile(file);
                              }
                            }}
                          />
                        </label>
                      </div>

                      {audioFile && (
                        <div className="flex items-center gap-3 mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                          <button
                            type="button"
                            onClick={() => {
                              if (!uploadedAudioRef.current) return;

                              if (isUploadedAudioPlaying) {
                                uploadedAudioRef.current.pause();
                                setIsUploadedAudioPlaying(false);
                              } else {
                                // Create a new audio URL and set it before playing
                                const audioUrl = URL.createObjectURL(audioFile);
                                uploadedAudioRef.current.src = audioUrl;
                                uploadedAudioRef.current.load(); // Load the audio

                                uploadedAudioRef.current
                                  .play()
                                  .then(() => setIsUploadedAudioPlaying(true))
                                  .catch((err) => {
                                    console.error("Playback failed:", err);
                                    toast.error("Failed to play audio");
                                  });
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors"
                          >
                            {isUploadedAudioPlaying ? (
                              <Pause size={14} />
                            ) : (
                              <Play size={14} className="ml-0.5" />
                            )}
                          </button>

                          <audio
                            ref={uploadedAudioRef}
                            preload="auto"
                            onEnded={() => setIsUploadedAudioPlaying(false)}
                            onError={(e) => {
                              console.error("Audio error:", e);
                              toast.error("Error loading audio");
                            }}
                          />

                          <span className="text-xs text-gray-600 truncate flex-1">
                            {audioFile.name}
                          </span>

                          <button
                            type="button"
                            onClick={() => {
                              if (uploadedAudioRef.current) {
                                uploadedAudioRef.current.pause();
                                uploadedAudioRef.current.removeAttribute("src"); // Clear the src
                                uploadedAudioRef.current.load();
                                setIsUploadedAudioPlaying(false);
                              }
                              setAudioFile(null);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Supported formats: MP3, WAV, M4A, AAC, OGG, FLAC, WEBM,
                        3GP, and more (max 50MB)
                      </p>
                    </>
                  )}

                  {/* Show existing audio info when locked */}
                  {locked && existingAudio?.url && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Audio file:</span>{" "}
                      {existingAudio.filename}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Website Link{" "}
                    <span className="text-sm font-normal text-gray-500">
                      (ex: https://digikorejingles.com/)
                    </span>
                  </label>
                  <input
                    type="url"
                    {...register("website")}
                    disabled={locked || loading}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Instagram Link
                    <span className="text-sm font-normal text-gray-500">
                      (ex: https://www.instagram.com/digikore_studios/)
                    </span>
                  </label>
                  <input
                    type="url"
                    {...register("instagramLink")}
                    disabled={locked || loading}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter details About the Company, Products, or Vision{" "}
                    <span className="text-red-600">*</span>
                    <span className="text-sm font-normal text-gray-500">
                      (ex: Cafe, Food and Beverages)
                    </span>
                  </label>
                  <textarea
                    {...register("companyDetails", {
                      required: "Company details are required",
                    })}
                    rows={2}
                    disabled={locked || loading}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                      errors.companyDetails
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.companyDetails && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.companyDetails.message}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                Jingle Requirements
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Preferred Language for Jingle{" "}
                    <span className="text-red-600">*</span>
                    <span className="text-sm font-normal text-gray-500">
                      (ex: English / Hindi)
                    </span>
                  </label>
                  <input
                    type="text"
                    {...register("preferredLanguage", {
                      required: "Preferred language is required",
                    })}
                    disabled={locked || loading}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                      errors.preferredLanguage
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.preferredLanguage && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.preferredLanguage.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Select your Industry Category{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <Controller
                    name="industryCategory"
                    control={control}
                    rules={{ required: "Industry category is required" }}
                    render={({ field }) => (
                      <>
                        <select
                          {...field}
                          disabled={locked || loading}
                          className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                            errors.industryCategory
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">Select category</option>
                          {industryCategories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                        {errors.industryCategory && (
                          <p className="text-red-600 text-xs mt-1">
                            {errors.industryCategory.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Select your Target Audience{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <Controller
                    name="targetAudience"
                    control={control}
                    rules={{
                      required: "Please select at least one target audience",
                      validate: (value) =>
                        (value && value.length > 0) ||
                        "Please select at least one target audience",
                    }}
                    render={({ field }) => (
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className={`w-full px-2.5 py-1.5 border rounded text-sm text-left focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                            errors.targetAudience
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300"
                          } ${isLoading ? "bg-gray-100" : ""}`}
                          disabled={isLoading || locked || loading}
                        >
                          <div className="flex justify-between items-center">
                            <span className="truncate">
                              {field.value?.length > 0
                                ? field.value.join(", ")
                                : "Select target audience"}
                            </span>
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                isDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </button>

                        {isDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                            {targetAudienceOptions.map((option) => (
                              <label
                                key={option}
                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={field.value?.includes(option)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      field.onChange([...field.value, option]);
                                    } else {
                                      field.onChange(
                                        field.value.filter(
                                          (value) => value !== option,
                                        ),
                                      );
                                    }
                                  }}
                                  className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                  disabled={isLoading || locked || loading}
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  {option}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                        {errors.targetAudience && (
                          <p className="text-red-600 text-xs mt-1">
                            {errors.targetAudience.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Select your Tone for Jingle{" "}
                    <span className="text-red-600">* </span>
                    <span className="text-sm font-normal text-gray-500">
                      (Listen to tone samples in dropdown and select tone you
                      prefer for your jingle)
                    </span>
                  </label>

                  <Controller
                    name="tone"
                    control={control}
                    rules={{ required: "Tone is required" }}
                    render={({ field }) => (
                      <div className="relative" ref={toneDropdownRef}>
                        <button
                          type="button"
                          onClick={() =>
                            setIsToneDropdownOpen(!isToneDropdownOpen)
                          }
                          className={`w-full px-2.5 py-1.5 border rounded text-sm text-left 
                focus:outline-none focus:ring-1 focus:ring-orange-500
                ${errors.tone ? "border-red-300 bg-red-50" : "border-gray-300"}`}
                          disabled={locked}
                        >
                          <div className="flex justify-between items-center">
                            <span>{field.value || "Select tone"}</span>
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                isToneDropdownOpen ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </button>

                        {isToneDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg">
                            {toneOptions.map((t) => (
                              <div
                                key={t.name}
                                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                                onClick={() => {
                                  field.onChange(t.name);
                                  setIsToneDropdownOpen(false);
                                }}
                              >
                                <span
                                  className="cursor-pointer text-sm"
                                  onClick={() => {
                                    field.onChange(t.name);
                                    setIsToneDropdownOpen(false);
                                  }}
                                >
                                  {t.name}
                                </span>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePlay(t);
                                  }}
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  {playingTone === t.name ? "⏸" : "▶"}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {errors.tone && (
                          <p className="text-red-600 text-xs mt-1">
                            {errors.tone.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Select your Voice Preference for jingle{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  <Controller
                    name="voicePreference"
                    control={control}
                    rules={{ required: "Voice preference is required" }}
                    render={({ field }) => (
                      <>
                        <select
                          {...field}
                          disabled={locked || loading}
                          className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                            errors.voicePreference
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300"
                          }`}
                        >
                          <option value="">Select voice</option>
                          {voiceOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                        {errors.voicePreference && (
                          <p className="text-red-600 text-xs mt-1">
                            {errors.voicePreference.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Tagline to include in jingle{" "}
                    <span className="text-red-600">*</span>
                    <span className="text-sm font-normal text-gray-500">
                      (ex: Washing powder Nirma)
                    </span>
                  </label>
                  <input
                    type="text"
                    {...register("tagline", {
                      required: "Tagline is required",
                    })}
                    disabled={locked || loading}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                      errors.tagline
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.tagline && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.tagline.message}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter your Keywords you want to include in jingle
                    (comma-separated) <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    {...register("keywords", {
                      required: "Keywords are required",
                    })}
                    rows={2}
                    disabled={locked || loading}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                      errors.keywords
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.keywords && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.keywords.message}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Enter Restrictions idf any else
                    <span className="text-red-600">*</span>
                    <span className="text-sm font-normal text-gray-500">
                      (ex: Cast religion politics gender am any thing that will
                      not be accepted by the audience society or government.
                      Enter none if there are no restrictions.)
                    </span>
                  </label>
                  <textarea
                    {...register("restrictions", {
                      required: "Restrictions are required",
                    })}
                    rows={2}
                    disabled={locked || loading}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                      errors.restrictions
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.restrictions && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.restrictions.message}
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>

          <div className="flex items-center justify-end gap-2 mt-6 border-t border-gray-200 pt-4">
            <button
              type="submit"
              disabled={locked || submitting || loading || orderNotFound}
              className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? "Submitting..."
                : locked
                  ? "Submitted"
                  : "Submit & Lock"}
            </button>
          </div>
        </form>
      </div>

      {/* Custom slider styles */}
      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #f97316;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #f97316;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default ClientJingleDetailsForm;
